/* import __COLOCATED_TEMPLATE__ from './ecommerce-upsell-banner.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type AppService from 'embercom/services/app-service';
import { get } from 'embercom/lib/ajax';
/** Workflow object types: CUSTOM_BOT, INBOUND_CUSTOM_BOT, BUTTON_CUSTOM_BOT, TRIGGERABLE_CUSTOM_BOT */
const WORKFLOW_OBJECT_TYPES = [54, 62, 63, 104];

interface Signature {
  Args: {};
  Element: HTMLDivElement;
}

export default class EcommerceUpsellBanner extends Component<Signature> {
  @service declare appService: AppService;

  @tracked isEligible = false;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    void this.checkEligibility();
  }

  get appId(): string {
    return this.appService.app.id;
  }

  get getStartedUrl(): string {
    return `/a/apps/${this.appId}/automation/fin-ai-agent/ecommerce/get-started`;
  }

  private async checkEligibility(): Promise<void> {
    try {
      let [statuses, shopifyIntegrations, serviceWorkflows, ecommerceWorkflows] = await Promise.all(
        [
          get('/teammate_app/agent_deployment_statuses', { app_id: this.appId }),
          get('/ember/shopify/integrations', { app_id: this.appId }),
          this.searchWorkflows('has_fin_service_agent'),
          this.searchWorkflows('has_fin_ecommerce_agent'),
        ],
      );

      let shopifyConnected = Array.isArray(shopifyIntegrations) && shopifyIntegrations.length > 0;
      let hasServiceWorkflows = (serviceWorkflows?.total_count ?? 0) > 0;
      let hasEcommerceWorkflows = (ecommerceWorkflows?.total_count ?? 0) > 0;

      this.isEligible =
        shopifyConnected &&
        statuses.support === true &&
        statuses.ecommerce !== true &&
        hasServiceWorkflows &&
        !hasEcommerceWorkflows;
    } catch {
      // Silently fail — banner is non-critical UI
      this.isEligible = false;
    }
  }

  private async searchWorkflows(additionalSearchKey: string): Promise<{ total_count: number }> {
    let params = new URLSearchParams();
    WORKFLOW_OBJECT_TYPES.forEach((type) => params.append('object_types[]', type.toString()));
    params.append('per_page', '1');
    params.append('sort_by', 'priority');
    params.append('sort_direction', 'asc');
    params.append(`additional_searchable_data[${additionalSearchKey}]`, 'true');
    params.append('app_id', this.appId);

    return get(`/ember/content_service/contents/search?${params.toString()}`);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'FinEcommerceAgent::EcommerceUpsellBanner': typeof EcommerceUpsellBanner;
    'fin-ecommerce-agent/ecommerce-upsell-banner': typeof EcommerceUpsellBanner;
  }
}
